// =====================================================================================================================
// LOGO VIEW
//
//  The portfolio landing page
//

import { EventManager, VertContainer, Ui, GridContainer} from "@stratisopt/platform";
import { UiFactory } from "@stratisopt/platform";

export class ProjectArea extends GridContainer {

    // =================================================================================================================
    // CONSTRUCTORS
    //

    // Create and initialize a new instance
    public static create(json:any):ProjectArea {
        let ui = new ProjectArea();
        ui.init(json);
        return ui;
    }

    // Initialize this instance
    public init(json:any) {

        // Mine out nav item specific properties
        const name = json.name;
        const title = json.title;
        const overview = json.overview;

        const resourceContent = json.resourceContent;
        const imageFolder = json.imageFolder;
        const imageData = json.imageData;
        const navPath = json.navPath;

        let newJson = this.createJson(name, title, overview, resourceContent, imageFolder, imageData, navPath);

        // Initialize the super
        super.init(newJson);
        UiFactory.addChildren(this, newJson);

        // Update with json overrides (if they exist)
        if (json !== undefined) {
            this.setFieldsFromJson(json);           
        }
    }


    // =================================================================================================================
    // VIEW
    //
    private createResourceArea(resourceContent:any):any{

        let resourceChildren:any[] = [];
        if (Array.isArray(resourceContent) === true) {
            resourceChildren = resourceContent;
        } else {
            resourceChildren = [resourceContent];
        }

        let json:any = {
            type:'H_CONTAINER', sizeX:'1*', alignContentY:'mid', pad:Ui.theme.SPACING_Nest, margin:Ui.theme.SPACING_Nest,borderRadius:Ui.theme.BORDER_Rounded, backColor:Ui.theme.COLOR_ProjectsWindow, 
            children:[
                {type:'text', pad:Ui.theme.SPACING_Nest, textSize:Ui.theme.SIZE_FontNeutral,
                    value:"Resources"
                },
                {type:'W_CONTAINER', sizeX:'1*', alignContentX:'max', children:
                    resourceChildren
                }
            ]
        };
        return json;
    }

    // True when there is at least one resource to render
    private hasResourceData(resourceContent:any):boolean {

        if (resourceContent === undefined) return false;
        if (resourceContent === null) return false;

        if (Array.isArray(resourceContent) === true) {
            return resourceContent.length > 0;
        }

        return true;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // IMAGES AREA
    //
    //  Built from the project's image data rather than being hand-authored. imageData is an array of
    //  { path, description } where path is relative to imageFolder.
    //

    // True when there is at least one image to render
    private hasImageData(imageData:any):boolean {

        if (imageData === undefined) return false;
        if (imageData === null) return false;

        if (Array.isArray(imageData) === true) {
            return imageData.length > 0;
        }

        return false;
    }

    // Join an image folder and a relative path into a single asset path
    private buildImagePath(imageFolder:string, path:string):string {

        if (imageFolder === undefined) return this.toAbsoluteAsset(path);
        if (imageFolder === null) return this.toAbsoluteAsset(path);
        if (imageFolder === "") return this.toAbsoluteAsset(path);
        if (path === undefined) return this.toAbsoluteAsset(imageFolder);
        if (path === null) return this.toAbsoluteAsset(imageFolder);

        let folder = imageFolder;
        if (folder.endsWith("/") === true) {
            folder = folder.substring(0, folder.length - 1);
        }

        return this.toAbsoluteAsset(folder + "/" + path);
    }

    // Force an asset path to be absolute (rooted at "/"). A relative path resolves against the current
    // document directory, so a deep route like /Projects/PandaMug shifts the base and 404s every image.
    // This matches the absolute /assets/... paths used elsewhere in the app.
    private toAbsoluteAsset(assetPath:string):string {
        if (assetPath === undefined) return assetPath;
        if (assetPath === null) return assetPath;
        if (assetPath === "") return assetPath;
        if (assetPath.startsWith("/") === true) return assetPath;
        if (assetPath.startsWith("http") === true) return assetPath;
        return "/" + assetPath;
    }

    // Per-project modal element names, kept in one place so the tile action and the modal build agree.
    private modalName(name:string):string {return name + "Modal";}
    private modalTabsName(name:string):string {return name + "ModalTabs";}
    private modalPaneName(name:string, index:number):string {return name + "ModalPane" + index;}
    private modalPrevName(name:string):string {return name + "ModalPrev";}
    private modalNextName(name:string):string {return name + "ModalNext";}

    // Build the onTouch action for an image tile: select the matching pane in the project's modal, then
    // show (clone) the modal. The description now rides inside the pane as plain text, so - unlike the old
    // single shared modal - nothing has to be escaped into the action string.
    private createTileAction(name:string, index:number):string {
        let action = "";
        action = action + "Ui.Select " + this.modalPaneName(name, index) + "; ";
        action = action + "UI.showModal " + this.modalName(name) + ";";
        return action;
    }

    // Build a single clickable image tile. name/index identify the modal pane this tile opens; sizeProps
    // supplies the per-tile layout (sizeX, sizeY, sizeMinY, ...)
    private createImageTile(name:string, index:number, fullPath:string, sizeProps:any):any {

        let tile:any = {
            type:'image',
            cursor:'pointer',
            resistanceX:'120px',
            backPos:'mid mid',
            pad:Ui.theme.SPACING_Nest,
            borderRadius:Ui.theme.BORDER_Rounded,
            backFit:'cover',
            value:fullPath,
            onTouch:this.createTileAction(name, index)
        };

        // Merge the per-tile layout properties over the defaults
        if (sizeProps !== undefined && sizeProps !== null) {
            for (const key of Object.keys(sizeProps)) {
                tile[key] = sizeProps[key];
            }
        }

        return tile;
    }

    // Choose the container arrangement for the given images.
    //  This is the single place the layout strategy lives. Today it is "feature + grid": the first image is a
    //  large hero and the rest fill a wrapping grid of equal tiles. A curated-per-count or randomized strategy
    //  would slot in here without touching the rest of the build.
    private createImageLayout(name:string, imageFolder:string, imageData:any[]):any {

        // A lone image becomes the full-width feature (pane index 0)
        if (imageData.length === 1) {
            const only = imageData[0];
            const onlyPath = this.buildImagePath(imageFolder, only.path);
            return this.createImageTile(name, 0, onlyPath, {sizeX:'1*', sizeY:'1*', resistanceX:'420px', sizeMinY:'320px'});
        }

        // Hero (the first image, pane index 0)
        const hero = imageData[0];
        const heroPath = this.buildImagePath(imageFolder, hero.path);
        const heroTile = this.createImageTile(name, 0, heroPath, {sizeX:'1*', sizeY:'1*', resistanceX:'420px',  sizeMinY:'320px'});

        // The remaining images as a wrapping grid of equal tiles (pane indices 1..n)
        let gridTiles:any[] = [];
        for (let i = 1; i < imageData.length; i++) {
            const item = imageData[i];
            const itemPath = this.buildImagePath(imageFolder, item.path);
            const tile = this.createImageTile(name, i, itemPath, {sizeX:'1*', resistanceX:'140px',sizeY:'160px'});
            gridTiles.push(tile);
        }

        let json:any = {
            type:'W_CONTAINER', sizeX:'1*', children:[
                heroTile,
                {type:'V_CONTAINER', sizeX:'1*', resistanceX:'240px', children:[
                    {type:'W_CONTAINER', sizeX:'1*', children:gridTiles}
                ]}
            ]
        };
        return json;
    }

    // Build the framed "Images" section that wraps the chosen layout
    private createImagesArea(name:string, imageFolder:string, imageData:any[]):any {

        let json:any = {
            type:'V_CONTAINER', sizeX:'1*', margin:Ui.theme.SPACING_Nest, pad:Ui.theme.SPACING_Nest, borderRadius:Ui.theme.BORDER_Rounded, backColor:Ui.theme.COLOR_ProjectsWindow,
            children:[
                {type:'W_CONTAINER', sizeX:'1*', children:[
                    {type:'text', padL:Ui.theme.SPACING_Nest, padR:Ui.theme.SPACING_Nest, padT:Ui.theme.SPACING_Nest, sizeX:'1*', sizeMinX:'content',textWrap:'true', textSize:Ui.theme.SIZE_FontNeutral,
                        value:"Images"
                    },
                    {type:'text', padL:Ui.theme.SPACING_Nest, padR:Ui.theme.SPACING_Nest, padT:Ui.theme.SPACING_Nest, textColor:Ui.theme.COLOR_FontEmphasize,textSize:Ui.theme.SIZE_FontNeutral,
                        value:"touch images for details"
                    },
                ]},
               
                this.createImageLayout(name, imageFolder, imageData)
            ]
        };
        return json;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // IMAGE MODAL
    //
    //  A per-project full-screen modal: a tab container of one image+description pane per image, stepped
    //  through by prev/next arrows. Declared collapsed inline; UI.showModal clones it into the adorner layer
    //  on open (carrying the tile-selected pane + the arrows' opacity), so there is no app-side state to keep.
    //
    //  The arrows are deliberately a plain row here - they are fully wired and self-dimming at the ends, ready
    //  to be relocated into the floating overlay that owns the modal's close affordance.
    //

    // Build one pane: the contained image and its description text, shown only when this pane is selected.
    private createModalPane(name:string, index:number, fullPath:string, description:string, tabsName:string):any {

        // The first pane is the default selection so the freshly-cloned modal has something to show.
        let selected:string = undefined;
        if (index === 0) {
            selected = 'true';
        }

        let pane:any = {
            type:'V_CONTAINER', name:this.modalPaneName(name, index), selected:selected,  
            sizeX:'1*', sizeY:'1*', scrollshowY:'true', alignContentX:'mid', alignContentY:'mid', textSize:Ui.theme.SIZE_FontNeutral,
            children:[
                {type:'text', sizeX:'1*', textSize:Ui.theme.SIZE_FontNeutral, textAlign:'mid', textWrap:'true', textColor:Ui.theme.COLOR_FontEmphasize, pad:Ui.theme.SPACING_Nest,value:'touch anywhere to dismiss'},
                {type:'image', sizeX:'1*', sizeY:'1*', sizeMinY:'360px', backFit:'contain', backColor:'black', onSwipeMaxX:'UI.SelectPrior '+tabsName, onSwipeMinX:'UI.SelectNext '+tabsName,
                    borderRadius:Ui.theme.BORDER_Rounded, value:fullPath},
                {type:'V_CONTAINER', sizeX:'1*', alignContentX:'mid', children:[
                    {type:'text', sizeX:'1*', pad:Ui.theme.SPACING_Nest, textAlign:'mid', textWrap:'true', textColor:Ui.theme.COLOR_FontNeutral,
                        value:description},
                ]}
            ]
        };
        return pane;
    }

    private createImageModal(name:string, imageFolder:string, imageData:any[]):any {

        const tabsName = this.modalTabsName(name);
        const prevName = this.modalPrevName(name);
        const nextName = this.modalNextName(name);

        // Disabled / enabled look for the step arrows. The verbs already no-op at the ends (no wrap); this
        // is purely the visual cue. Multi-image projects also drive these live via the bounds events below.
        const dimDisabled = '0.3';
        const dimEnabled = '1';

        // One pane per image.
        let panes:any[] = [];
        for (let i = 0; i < imageData.length; i++) {
            const item = imageData[i];
            const fullPath = this.buildImagePath(imageFolder, item.path);
            panes.push(this.createModalPane(name, i, fullPath, item.description, tabsName));
        }

        // The carousel. Only the selected pane shows. For a multi-image set, the container reports where the
        // selection sits (onMin/onMid/onMax) so the arrows dim at the boundaries; min and max never coincide
        // when there are 2+ panes, so the handlers never fight. A lone image has no boundaries to cross, so it
        // skips the wiring and just shows both arrows statically dimmed (handled below).
        let tabs:any = {
            type:'T_CONTAINER', name:tabsName, sizeX:'1*', sizeY:'1*', onTouch:'Ui.exit', children:panes
        };
        if (imageData.length > 1) {
            tabs.onMinSelected = 'Ui.set ' + prevName + ', opacity, ' + dimDisabled + '; Ui.set ' + nextName + ', opacity, ' + dimEnabled;
            tabs.onMaxSelected = 'Ui.set ' + nextName + ', opacity, ' + dimDisabled + '; Ui.set ' + prevName + ', opacity, ' + dimEnabled;
            tabs.onMidSelected = 'Ui.set ' + prevName + ', opacity, ' + dimEnabled + '; Ui.set ' + nextName + ', opacity, ' + dimEnabled;
        }

        // Base arrow opacity reflects the default selection (pane 0 = at the min edge): prev disabled, next
        // enabled. This is also the fallback when a tile re-selects the already-selected hero (no event fires).
        // A lone image is at both edges at once, so both arrows start disabled.
        let nextBaseOpacity:string = dimEnabled;
        if (imageData.length === 1) {
            nextBaseOpacity = dimDisabled;
        }

        let json:any = {
            type:'G_CONTAINER', name:this.modalName(name), visible:'collapsed', sizeX:'1*', sizeY:'1*', 
            backColor:Ui.theme.COLOR_ProjectsWindow,
            children:[
                tabs,
                {type:'V_CONTAINER', sizeX:'1*', interactable:'false', children:[
                    {type:'text', sizeX:'1*', opacity:'0', textSize:Ui.theme.SIZE_FontNeutral, textAlign:'mid', textWrap:'true', textColor:Ui.theme.COLOR_FontEmphasize, pad:Ui.theme.SPACING_Nest,value:'touch anywhere to dismiss'},
                    {type:'G_CONTAINER', sizeX:'1*', children:[
                        {type:'V_CONTAINER', interactable:'true', cursor:'pointer', alignSelfX:'min', borderRadius:Ui.theme.BORDER_Rounded, effect:'hover', pad:Ui.theme.SPACING_Neutral, backColor:Ui.theme.COLOR_ProjectsWindow, onTouch:'UI.SelectPrior ' + tabsName, 
                        children:[
                            {type:'text', name:prevName, opacity:dimDisabled,
                            textColor:Ui.theme.COLOR_FontEmphasize, textSize:Ui.theme.SIZE_FontEmphasize, value:'‹ prev'},
                        ]},
                        {type:'V_CONTAINER', interactable:'true', cursor:'pointer', alignSelfX:'max', borderRadius:Ui.theme.BORDER_Rounded, effect:'hover', pad:Ui.theme.SPACING_Neutral, backColor:Ui.theme.COLOR_ProjectsWindow, onTouch:'UI.SelectNext ' + tabsName,
                            children:[
                                {type:'text', name:nextName, opacity:nextBaseOpacity,
                                textColor:Ui.theme.COLOR_FontEmphasize, textSize:Ui.theme.SIZE_FontEmphasize, value:'next ›'},
                        ]},
                    ]}
                ]},
            ]
        };
        return json;
    }

    private createJson(name:string = "", title:string = "No title given", overview:string = "", resourceContent:any = undefined, imageFolder:string = "", imageData:any = undefined, navPath:string =""):any {

        let children:any[] = [
            {type:'V_CONTAINER', textLineHeight:'1.5', sizeX:'1*', margin:Ui.theme.SPACING_Nest, pad:Ui.theme.SPACING_Nest, borderRadius:Ui.theme.BORDER_Rounded, backColor:Ui.theme.COLOR_ProjectsWindow,
            children:[
                // Title area
                {type:'text', pad:Ui.theme.SPACING_Nest, marginB:Ui.theme.SPACING_Neutral, sizeX:'1*', textWrap:'true', textSize:Ui.theme.SIZE_FontEmphasize, value:title},

                // Overview area
                {type:'text', padL:Ui.theme.SPACING_Nest, padR:Ui.theme.SPACING_Nest, padT:Ui.theme.SPACING_Nest,sizeX:'1*', textWrap:'true', textSize:Ui.theme.SIZE_FontNeutral, value:"Overview"},
                {type:'text', markdown:'true', pad:Ui.theme.SPACING_Nest, sizeX:'1*', textWrap:'true', textSize:Ui.theme.SIZE_FontNeutral,value:overview},
            ]},
        ];

        // Only show the images area when there are images to display
        if (this.hasImageData(imageData) === true) {
            children.push(this.createImagesArea(name, imageFolder, imageData));
        }

        // Only show the resources area when there is resource data to display
        if (this.hasResourceData(resourceContent) === true) {
            children.push(this.createResourceArea(resourceContent));
        }

        // The G_CONTAINER acts as an overlay: the content stack plus the collapsed image modal, which is
        // cloned out into the adorner layer on open. The modal is only added when there are images to show.
        let rootChildren:any[] = [
            {type:'V_CONTAINER', sizeX:'1*', children:children},
        ];
        if (this.hasImageData(imageData) === true) {
            rootChildren.push(this.createImageModal(name, imageFolder, imageData));
        }

        let json:any = {
            type:'G_CONTAINER', name:name, sizeX:'1*', sizeY:'1*', scrollShowY:'true', onSelect:'UI.Select '+navPath,
            children:rootChildren
        };
        return json;
    }

}
